/**
 * TradeConfirmation component - Inline trade confirmation card within the chat.
 * Displays trade details with Confirm/Cancel actions.
 * Transitions in-place to show confirmed, cancelled, or expired states.
 */
import { clsx } from 'clsx';
import type { PendingTrade } from '../../types/chat';

/** Format P&L as a single string: +$1,250.00 or -$500.25 */
function formatPnl(value: number): string {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (value >= 0) return `+$${formatted}`;
  return `-$${formatted}`;
}

interface TradeConfirmationProps {
  trade: PendingTrade;
  onConfirm: () => void;
  onCancel: () => void;
  isExpired: boolean;
}

export function TradeConfirmation({
  trade,
  onConfirm,
  onCancel,
  isExpired,
}: TradeConfirmationProps) {
  const isBuy = trade.action === 'BUY';
  const totalValue = trade.quantity * trade.price;
  const status = trade.status || (isExpired ? 'expired' : 'pending');
  const isResolved = status !== 'pending';

  return (
    <div
      className={clsx(
        'rounded-lg border p-4 transition-all duration-300',
        'bg-dark-800/90 backdrop-blur-sm',
        status === 'confirmed' && 'border-emerald-600/50',
        status === 'cancelled' && 'border-dark-700/30 opacity-70',
        status === 'expired' && 'border-dark-700/30 opacity-60',
        status === 'pending' && 'border-dark-600/50'
      )}
      role="region"
      aria-label={`Trade confirmation: ${trade.action} ${trade.quantity} shares of ${trade.symbol}`}
    >
      {/* Header: Symbol + Action Badge + Status Badge */}
      <div className="mb-3 flex items-center gap-3">
        <span className="text-xl font-bold text-dark-100">{trade.symbol}</span>
        <span
          className={clsx(
            'rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase',
            isBuy
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'bg-rose-500/20 text-rose-400'
          )}
        >
          {trade.action}
        </span>
        {status === 'confirmed' && (
          <span className="flex items-center gap-1 rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-xs font-semibold text-emerald-400">
            <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
            </svg>
            Confirmed
          </span>
        )}
        {status === 'confirmed' && trade.washSale && (
          <span className="rounded-full bg-blue-500/20 px-2.5 py-0.5 text-xs font-semibold text-blue-400">
            Wash Sale
          </span>
        )}
        {status === 'pending' && trade.washSale && (
          <span className="rounded-full bg-amber-500/20 px-2.5 py-0.5 text-xs font-semibold text-amber-400">
            ⚠ Wash Sale
          </span>
        )}
        {status === 'cancelled' && (
          <span className="rounded-full bg-dark-600/50 px-2.5 py-0.5 text-xs font-semibold text-dark-400">
            Cancelled
          </span>
        )}
        {status === 'expired' && (
          <span className="rounded-full bg-dark-600/50 px-2.5 py-0.5 text-xs font-semibold text-dark-400">
            Expired
          </span>
        )}
      </div>

      {/* Trade Details */}
      <div className={clsx('space-y-1.5 text-sm', !isResolved && 'mb-3')}>
        <div className="flex justify-between">
          <span className="text-dark-400">Quantity</span>
          <span className="font-medium text-dark-200">
            {trade.quantity.toLocaleString()} shares
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-dark-400">Price</span>
          <span className="font-medium text-dark-200">
            ${trade.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-dark-400">Total</span>
          <span className="font-medium text-dark-200">
            ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-dark-400">Position Impact</span>
          <span className="font-medium text-dark-200">{trade.impact}</span>
        </div>

        {/* FIFO P&L Estimate (sells only) */}
        {trade.estimatedPnl !== null && (
          <div className="flex justify-between border-t border-dark-700/50 pt-1.5">
            <span className="text-dark-400">Est. Realized P&L (FIFO)</span>
            <span
              className={clsx(
                'font-semibold',
                trade.estimatedPnl >= 0 ? 'text-emerald-400' : 'text-rose-400'
              )}
            >
              {formatPnl(trade.estimatedPnl)}
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons — only shown when pending */}
      {!isResolved && (
        <div className="flex gap-2">
          <button
            onClick={onConfirm}
            aria-label={`Confirm trade: ${trade.action} ${trade.quantity} ${trade.symbol} at $${trade.price}`}
            className="flex-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-emerald-500 active:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
          >
            Confirm Trade
          </button>
          <button
            onClick={onCancel}
            aria-label="Cancel trade"
            className="flex-1 rounded-lg bg-dark-700 px-4 py-2 text-sm font-medium text-dark-200 transition-all duration-200 hover:bg-dark-600 active:bg-dark-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
